import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { OpenInBuilder } from "@/components/quote/LinkBar";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { inputCls } from "@/lib/billing";
import { Btn, Card, PortalPage, Spinner } from "@/components/PortalPage";
import { CustomerPicker } from "@/pages/billing/DocEditor";
import { LinesEditor, linesPayload, newLine } from "@/pages/billing/Lines";
import { NotEnabled, useBillingStatus } from "@/pages/billing/BillingHome";
import { ScheduleFields, schedState, useTechs } from "@/pages/billing/Schedule";
import { RecordTools } from "@/components/billing/RecordTools";
import { ActivityTimeline } from "@/components/billing/ActivityTimeline";
import { ChangeOrders } from "@/components/billing/ChangeOrders";

const STATUSES = [["unscheduled", "Unscheduled"], ["scheduled", "Scheduled"], ["in_progress", "In progress"], ["completed", "Completed"]];

export default function JobEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const [st] = useBillingStatus();
  const [job, setJob] = useState(null);
  const [f, setF] = useState({ customer_id: "", title: "", scheduled_date: "", status: "unscheduled", notes: "" });
  const [lines, setLines] = useState([newLine()]);
  const [busy, setBusy] = useState("");
  const [tick, setTick] = useState(0);
  const [sched, setSched] = useState(schedState(null));
  const [custEmail, setCustEmail] = useState({});
  const techs = useTechs();
  useEffect(() => { api("/billing/customers").then((r) => setCustEmail(Object.fromEntries(r.map((c) => [c.id, c.email])))).catch(() => {}); }, []);
  const apply = useCallback((j) => {
    setJob(j);
    setSched(schedState(j));
    setF({ customer_id: j.customer_id, title: j.title, scheduled_date: j.scheduled_date || "", status: j.status, notes: j.notes || "" });
    setLines(j.lines.length ? j.lines : [newLine()]);
  }, []);
  useEffect(() => { if (id) api("/billing/jobs").then((rows) => { const j = rows.find((x) => x.id === id); if (j) apply(j); }).catch(() => {}); }, [id, apply]);
  if (!st) return <Spinner />;
  if (!st.enabled) return <NotEnabled />;
  if (id && !job) return <Spinner />;
  const act = async (name, fn) => { setBusy(name); try { await fn(); } catch (e) { toast.error(e.message); } finally { setBusy(""); } };
  const save = () => act("save", async () => {
    if (!f.customer_id || !f.title.trim()) throw new Error("Customer and job title are required");
    const body = { ...f, scheduled_date: sched.start_at?.slice(0, 10) || null, ...sched, lines: linesPayload(lines) };
    const j = await api(job ? `/billing/jobs/${job.id}` : "/billing/jobs", { method: job ? "PUT" : "POST", body });
    toast.success(sched.notify_customer ? (j.confirmation_sent ? "Job saved. Appointment emailed to the customer." : "Job saved, but the appointment email couldn't be sent.") : "Job saved");
    if (job) apply(j); else nav(`/billing/job/${j.id}`, { replace: true });
  });
  const invoice = () => act("inv", async () => { const d = await api(`/billing/jobs/${job.id}/invoice`, { method: "POST" }); nav(`/billing/doc/${d.id}`); });
  return (
    <PortalPage eyebrow="JOB" title={job ? `Job #${job.number}` : "New job"} back="/billing" width="max-w-4xl" testId="job-editor">
      <Card className="space-y-3">
        <CustomerPicker value={f.customer_id} onChange={(v) => setF({ ...f, customer_id: v })} />
        {job && <div className="mt-2"><OpenInBuilder customerId={job.customer_id} customerName={job.customer_name} jobId={job.id} jobNumber={job.number} jobTitle={job.title} /></div>}
        <input className={inputCls} placeholder="Job title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="job-title-input" />
        <p className="pt-1 text-[10px] font-bold tracking-[2px] text-[#777]">SCHEDULE</p>
        <ScheduleFields v={sched} setV={setSched} techs={techs} hasEmail={!!custEmail[f.customer_id]} />
        <div className="grid gap-2 md:grid-cols-2">
          <label className="text-xs text-[#888]">Status<select className={inputCls} value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} data-testid="job-status-select">{STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></label>
        </div>
        <LinesEditor lines={lines} setLines={setLines} docId={false} />
        <textarea className={inputCls} rows={2} placeholder="Job notes" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="job-notes-input" />
        <Btn onClick={save} busy={busy === "save"} data-testid="job-save-btn">{job ? "SAVE JOB" : "CREATE JOB"}</Btn>
        {job && <Btn variant="outline" onClick={invoice} busy={busy === "inv"} data-testid="job-invoice-btn">{job.invoice_id ? "OPEN INVOICE" : "CREATE INVOICE FROM JOB"}</Btn>}
      </Card>
      {job && <ChangeOrders job={job} reloadKey={tick} onChanged={() => setTick((t) => t + 1)} />}
      {job && <RecordTools kind="job" rec={job} onChange={setJob} />}
      {job && <ActivityTimeline scope="job" id={job.id} reloadKey={`${tick}-${job.invoice_id}-${job.status}`} />}
    </PortalPage>
  );
}
